import React, { useState, useRef } from 'react';
import './App.css';
import NoteDisplay from './components/NoteDisplay';
import TriadDisplay from './components/TriadDisplay';
import AudioManager from './components/AudioManager';
import KPIDisplay from './components/ScoreDisplay';
import GameControls, { PracticeMode } from './components/GameControls';
import { generateRandomNote, generateAdaptiveNote, generateRandomTriad, Triad } from './utils/noteUtils';
import audioPreloader from './utils/AudioPreloader';


function App() {
  // Audio preloading state
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [audioLoadingProgress, setAudioLoadingProgress] = useState(0);
  
  // Practice mode selection
  const [practiceMode, setPracticeMode] = useState<PracticeMode>('single-notes');
  const [isGameActive, setIsGameActive] = useState(false);
  
  // Single notes mode state
  const [currentTargetNote, setCurrentTargetNote] = useState<string>('');
  
  // Triads mode state
  const [currentTriad, setCurrentTriad] = useState<Triad | null>(null);
  const [triadProgress, setTriadProgress] = useState<number>(0); // 0, 1, 2, or 3 (how many notes completed)
  
  // Load sensitivity from localStorage, default to 1.0
  const [sensitivity, setSensitivity] = useState(() => {
    const saved = localStorage.getItem('fretboard-tuner-sensitivity');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Session duration tracking
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  const [sessionDuration, setSessionDuration] = useState<number>(0);

  // Visual feedback state
  const [noteFlashColor, setNoteFlashColor] = useState<'green' | 'red' | null>(null);
  const [isNoteTransitioning, setIsNoteTransitioning] = useState(false);
  const [gameState, setGameState] = useState<'playing' | 'success' | 'transition'>('playing');
  const [isProcessingSuccess, setIsProcessingSuccess] = useState(false);
  const lastDisplayedNoteRef = useRef<string>('');
  const currentTargetNoteRef = useRef<string>('');
  const currentTriadRef = useRef<Triad | null>(null);
  const triadProgressRef = useRef<number>(0);

  // Update session duration every second when game is active
  React.useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isGameActive && sessionStartTime > 0) {
      intervalId = setInterval(() => {
        setSessionDuration((Date.now() - sessionStartTime) / 1000);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isGameActive, sessionStartTime]);

  // Save sensitivity to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('fretboard-tuner-sensitivity', sensitivity.toString());
  }, [sensitivity]);

  // Preload audio files when app starts
  React.useEffect(() => {
    const preloadAudio = async () => {
      try {
        // Update progress during loading
        const progressInterval = setInterval(() => {
          const progress = audioPreloader.getLoadingProgress();
          setAudioLoadingProgress(progress.percentage);
        }, 100);

        await audioPreloader.preloadAll();
        
        clearInterval(progressInterval);
        setAudioLoadingProgress(100);
        
        // Brief delay to show 100% before hiding loader
        setTimeout(() => {
          setIsAudioLoading(false);
        }, 500);
      } catch (error) {
        console.warn('Failed to preload some audio files:', error);
        // Still allow app to work with fallback audio
        setIsAudioLoading(false);
      }
    };

    preloadAudio();
  }, []);

  const handleNoteDetected = (detectedNote: string, isCorrect: boolean, responseTime: number) => {
    // Prevent any processing if already processing a success
    if (isProcessingSuccess || gameState !== 'playing') {
      return;
    }

    if (practiceMode === 'single-notes') {
      handleSingleNoteDetected(detectedNote, isCorrect, responseTime);
    } else if (practiceMode === 'triads') {
      handleTriadNoteDetected(detectedNote, isCorrect, responseTime);
    }
  };

  const handleSingleNoteDetected = (detectedNote: string, isCorrect: boolean, responseTime: number) => {
    if (isCorrect) {

      // Immediately prevent any further processing
      setIsProcessingSuccess(true);
      setGameState('success');
      setNoteFlashColor('green');

      // Play correct feedback sound
      playCorrectSound();

      // Start transition sequence
      setTimeout(() => {
        setNoteFlashColor(null);
        setGameState('transition');
        setIsNoteTransitioning(true);
        startSingleNoteTransition();
      }, 500);

    } else {
      // Wrong note - flash red and play incorrect feedback sound
      setNoteFlashColor('red');
      playIncorrectSound();

      setTimeout(() => {
        setNoteFlashColor(null);
      }, 500);
    }
  };

  const handleTriadNoteDetected = (detectedNote: string, isCorrect: boolean, responseTime: number) => {
    const currentTriadValue = currentTriadRef.current;
    const currentProgress = triadProgressRef.current;
    if (!currentTriadValue) return;

    if (isCorrect) {
      // Correct note for current triad position
      setNoteFlashColor('green');
      playCorrectSound();
      
      // Move to next note in triad
      const newProgress = currentProgress + 1;
      setTriadProgress(newProgress);

      setTimeout(() => {
        setNoteFlashColor(null);
        
        if (newProgress < 3) {
          // More notes to play - announce next note
          const nextNote = currentTriadValue.notes[newProgress];
          setTimeout(() => {
            playNoteSound(nextNote);
          }, 200);
        } else {
          // Triad complete - start transition to new triad
          setIsNoteTransitioning(true);
          startTriadTransition();
        }
      }, 500);

    } else {
      // Wrong note - flash red and play incorrect feedback sound
      setNoteFlashColor('red');
      playIncorrectSound();

      setTimeout(() => {
        setNoteFlashColor(null);
      }, 500);
    }
  };

  const startSingleNoteTransition = () => {
    const currentTarget = currentTargetNoteRef.current; // Use ref to get current value
    
    // Store the current target note before clearing
    lastDisplayedNoteRef.current = currentTarget;
    // Clear the target note immediately to prevent further detection
    setCurrentTargetNote('');

    // Generate new note after a brief pause
    setTimeout(() => {
      const newNote = generateAdaptiveNote(lastDisplayedNoteRef.current, []); // Use adaptive selection with empty history
      
      setCurrentTargetNote(newNote);
      lastDisplayedNoteRef.current = newNote;
      setIsNoteTransitioning(false);
      setGameState('playing');
      setIsProcessingSuccess(false);
      
      // Play the reference tone for the new note
      playNoteSound(newNote);
    }, 1000);
  };

  const startTriadTransition = () => {
    // Clear current triad immediately to prevent further detection
    setCurrentTriad(null);
    setTriadProgress(0);

    // Generate new triad after a brief pause
    setTimeout(() => {
      const newTriad = generateRandomTriad();
      setCurrentTriad(newTriad);
      setIsNoteTransitioning(false);
      setGameState('playing');
      setIsProcessingSuccess(false);
      
      // Play triad announcement: "C# Major"
      setTimeout(() => {
        playTriadAnnouncement(newTriad);
      }, 100);
    }, 1000);
  };

  const noteToFrequency = (note: string): number => {
    // Normalize note (convert flats to sharps for frequency lookup)
    const normalizedNote = note.replace('b', '♭');
    const noteMap: { [key: string]: number } = {
      'C': 261.63,
      'C#': 277.18,
      'D♭': 277.18,
      'D': 293.66,
      'D#': 311.13,
      'E♭': 311.13,
      'E': 329.63,
      'F': 349.23,
      'F#': 369.99,
      'G♭': 369.99,
      'G': 392.00,
      'G#': 415.30,
      'A♭': 415.30,
      'A': 440.00,
      'A#': 466.16,
      'B♭': 466.16,
      'B': 493.88
    };
    return noteMap[normalizedNote] || 440; // Default to A if note not found
  };


  const playNoteSound = (note: string) => {
    // Try to play preloaded audio first
    const success = audioPreloader.playAudio(note, 0.7);
    
    if (!success) {
      // Fallback to original synthetic tone if preloaded audio fails
      console.warn('Preloaded audio failed, using fallback tone for:', note);
      playFallbackTone(note);
    }
  };

  const playFallbackTone = (note: string) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);

    const frequency = noteToFrequency(note);
    const oscillator = audioContext.createOscillator();
    oscillator.connect(gainNode);
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = 'sine'; // Clean sine wave for reference tone

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15); // Brief 150ms click

    // Quick fade out to avoid clicks
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);
  };

  const playCorrectSound = () => {
    const success = audioPreloader.playAudio('correct', 0.8);
    
    if (!success) {
      // Fallback to synthetic sound
      playFallbackCorrectSound();
    }
  };

  const playIncorrectSound = () => {
    const success = audioPreloader.playAudio('incorrect', 0.8);
    
    if (!success) {
      // Fallback to synthetic sound
      playFallbackErrorSound();
    }
  };

  const playFallbackCorrectSound = () => {
    // Pleasant ascending tone for correct answers
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

    const frequencies = [440, 554, 659]; // A, C#, E (A major chord)
    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      oscillator.connect(gainNode);
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      oscillator.type = 'sine';

      oscillator.start(audioContext.currentTime + index * 0.1);
      oscillator.stop(audioContext.currentTime + 0.1 + index * 0.1);
    });

    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.4);
  };

  const playFallbackErrorSound = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

    // Create harsh error sound - two dissonant frequencies
    const frequencies = [200, 150]; // Dissonant interval

    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      oscillator.connect(gainNode);
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      oscillator.type = 'triangle';

      oscillator.start(audioContext.currentTime + index * 0.05);
      oscillator.stop(audioContext.currentTime + 0.2 + index * 0.05);
    });

    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
  };

  const playTriadTypeSound = (type: 'major' | 'minor') => {
    const success = audioPreloader.playAudio(type, 0.7);
    
    if (!success) {
      console.warn(`Failed to play preloaded ${type} sound`);
    }
  };

  const playTriadAnnouncement = (triad: Triad) => {
    // Play the root note first
    playNoteSound(triad.root);
    
    // Get the duration of the root note audio and add a small buffer
    const rootDuration = audioPreloader.getAudioDuration(triad.root);
    const delayMs = rootDuration + 100; // Add 100ms buffer to avoid overlap
    
    // Play the triad type after the root note finishes
    setTimeout(() => {
      playTriadTypeSound(triad.type);
    }, delayMs);
  };

  const initializeGame = () => {
    if (isGameActive) {
      // Reset session tracking
      setSessionStartTime(Date.now());
      setSessionDuration(0);
      
      if (practiceMode === 'single-notes' && !currentTargetNote) {
        const newNote = generateRandomNote();
        setCurrentTargetNote(newNote);
        lastDisplayedNoteRef.current = newNote;

        // Play the reference tone for the new note
        setTimeout(() => playNoteSound(newNote), 100);
      } else if (practiceMode === 'triads' && !currentTriad) {
        // Initialize triads mode
        setTriadProgress(0);
        const newTriad = generateRandomTriad();
        setCurrentTriad(newTriad);
        
        // Play triad announcement: "C# Major"
        setTimeout(() => {
          playTriadAnnouncement(newTriad);
        }, 100);
      }
    }
  };

  const handleStopGame = () => {
    setIsGameActive(false);
    setCurrentTargetNote('');
    setCurrentTriad(null);
    setTriadProgress(0);
    setNoteFlashColor(null);
    setIsNoteTransitioning(false);
    setGameState('playing');
    setIsProcessingSuccess(false);
  };


  // Track target note changes and keep ref in sync
  React.useEffect(() => {
    currentTargetNoteRef.current = currentTargetNote;
  }, [currentTargetNote]);

  // Track triad changes and keep refs in sync
  React.useEffect(() => {
    currentTriadRef.current = currentTriad;
  }, [currentTriad]);

  React.useEffect(() => {
    triadProgressRef.current = triadProgress;
  }, [triadProgress]);

  // Initialize game when started
  React.useEffect(() => {
    initializeGame();
  }, [isGameActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show loading screen while audio is preloading
  if (isAudioLoading) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Learn the Fretboard</h1>
          <p>Learn guitar and bass fretboard notes</p>
        </header>
        <main className="App-main">
          <div className="loading-container">
            <div className="loading-content">
              <h2>Loading Audio Files...</h2>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${audioLoadingProgress}%` }}
                ></div>
              </div>
              <p>{audioLoadingProgress}% Complete</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Learn the Fretboard</h1>
        <p>Learn guitar and bass fretboard notes</p>
      </header>

      <main className="App-main">
        <GameControls
          isGameActive={isGameActive}
          onStartSingleNotes={() => {
            setPracticeMode('single-notes');
            setIsGameActive(true);
          }}
          onStartTriads={() => {
            setPracticeMode('triads');
            setIsGameActive(true);
          }}
          onStopGame={handleStopGame}
        />

        {practiceMode === 'single-notes' ? (
          <NoteDisplay
            targetNote={currentTargetNote}
            isGameActive={isGameActive}
            flashColor={noteFlashColor}
            isTransitioning={isNoteTransitioning}
          />
        ) : (
          <TriadDisplay
            currentTriad={currentTriad}
            isGameActive={isGameActive}
            completedNotes={triadProgress}
            flashColor={noteFlashColor}
            isTransitioning={isNoteTransitioning}
          />
        )}

        <KPIDisplay
          sessionDuration={sessionDuration}
        />

        <AudioManager
          isGameActive={isGameActive}
          targetNote={practiceMode === 'single-notes' 
            ? currentTargetNote 
            : (currentTriad && triadProgress < 3 ? currentTriad.notes[triadProgress] : '')
          }
          onNoteDetected={handleNoteDetected}
          gameState={gameState}
          sensitivity={sensitivity}
          onSensitivityChange={setSensitivity}
        />


      </main>
    </div>
  );
}

export default App;
